import { useState, useEffect } from 'react'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Modal from 'react-bootstrap/Modal'
import Table from 'react-bootstrap/Table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import { toast } from 'react-toastify'
import UsersDropdown from 'shared/forms/users-dropdown'
import { useCreateFolderUser, useDestroyFolderUser, useUpdateFolderUser } from 'api/hooks/use-folder-users'
import { useQueryClient } from 'react-query'
import MutedText from 'shared/forms/muted-text'

const FolderUserRow = ({ folderUser, folderId }) => {
  const queryClient = useQueryClient()
  const [isEditor, setIsEditor] = useState(folderUser.is_editor || false)

  const invalidate = () => {
    queryClient.invalidateQueries('folders')
  }

  const { mutate: destroyMutation, isLoading: isDestroying } = useDestroyFolderUser({
    id: folderUser.id,
    options: {
      onSuccess: () => {
        invalidate()
        toast.success(`Removed ${folderUser.first_name} ${folderUser.last_name}`)
      }
    }
  })

  const { mutate: updateMutation, isLoading: isUpdating } = useUpdateFolderUser({
    id: folderUser.id,
    options: {
      onSuccess: (data) => {
        if (data && data.is_editor !== undefined) {
          setIsEditor(data.is_editor)
        }
        invalidate()
      }
    }
  })

  const handleRoleChange = (e) => {
    updateMutation({ is_editor: e.target.value === 'editor' })
  }

  return (
    <tr>
      <td>
        <div className="d-flex align-items-center justify-content-between gap-2">
          <div>
            {folderUser.first_name} {folderUser.last_name}
            <MutedText as="div">
              <small>{folderUser.email}</small>
            </MutedText>
          </div>
          <Form.Select
            size="sm"
            id={`folder-editor-select-${folderUser.id}`}
            aria-label="Access role"
            value={isEditor ? 'editor' : 'viewer'}
            onChange={handleRoleChange}
            disabled={isUpdating || isDestroying}
            style={{ width: 'auto' }}
          >
            <option value="viewer">Viewer</option>
            <option value="editor">Editor</option>
          </Form.Select>
        </div>
      </td>
      <td className="text-end">
        <Button
          variant="light"
          className="bg-light"
          onClick={() => destroyMutation()}
          disabled={isDestroying || isUpdating}
        >
          <FontAwesomeIcon icon={faTrash} className="text-danger" />
        </Button>
      </td>
    </tr>
  )
}

const ShareFolderModal = ({ folder, show, onHide }) => {
  const queryClient = useQueryClient()
  const [userOption, setUserOption] = useState('')

  const sharedUserIds = folder?.shared_users?.map((u) => u.user_id) || []
  const allExcludedIds = folder ? sharedUserIds.concat(folder.user_id) : []

  const {
    mutate: createMutation,
    isLoading,
    isError,
    isSuccess
  } = useCreateFolderUser({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries('folders')
        toast.success('User added')
      }
    }
  })

  useEffect(() => {
    setUserOption('')
  }, [isSuccess])

  useEffect(() => {
    setUserOption('')
  }, [folder?.id])

  useEffect(() => {
    if (isError) toast.error('An error occurred')
  }, [isError])

  if (!folder) return null

  const inheritedCount = folder.inherited_shared_users_count || 0

  const handleAdd = () => {
    createMutation({
      user_id: userOption,
      folder_id: folder.id
    })
  }

  return (
    <Modal show={show} size="md" onHide={onHide}>
      <Modal.Header className="fw-light d-flex flex-row justify-content-between">
        <div className="d-flex fs-5">
          <span className="fw-normal me-2">Share {folder.name}</span>
        </div>
        <div className="d-flex">
          <Button variant="outline-primary" size="sm" onClick={onHide}>
            Close
          </Button>
        </div>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col sm={8}>
            <UsersDropdown
              params={userOption}
              setParams={setUserOption}
              isObject={false}
              disabled={allExcludedIds}
            />
          </Col>
          <Col sm={4}>
            <Button
              variant="primary"
              onClick={handleAdd}
              disabled={userOption === '' || isLoading}
            >
              <FontAwesomeIcon icon={faPlus} className="text-warning" /> Add User
            </Button>
          </Col>
        </Row>

        <h4 className="my-2">People with access</h4>
        <MutedText as="div" className="mb-2">
          <small>
            Access carries down: everyone listed here can also open this folder&apos;s subfolders.
            {inheritedCount > 0 && ` ${inheritedCount} more ${inheritedCount === 1 ? 'person reaches' : 'people reach'} this folder through the folder it sits in, and ${inheritedCount === 1 ? 'is' : 'are'} managed there.`}
          </small>
        </MutedText>
        <Table className="border border-light">
          <tbody className="border-top">
            <tr className="bg-light">
              <td colSpan={2}>
                {folder.owner.first_name} {folder.owner.last_name}
                <MutedText as="div">
                  <small>{folder.owner.email}</small>
                </MutedText>
                <small className="ms-1 text-muted">Owner</small>
              </td>
            </tr>
            {folder.shared_users?.map((folderUser) => (
              <FolderUserRow
                folderUser={folderUser}
                folderId={folder.id}
                key={`folder-user-${folderUser.id}`}
              />
            ))}
          </tbody>
        </Table>
      </Modal.Body>
    </Modal>
  )
}

export default ShareFolderModal
